/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import useMounted from '../../../shared/useMount/useMount';
import EmaasEnergyUsageComponent from '../EmaasEnergyUsageComponent/EmaasEnergyUsageComponent';
import {
  IAny,
  IEmaasEnergyUsageComponentDataArea,
  IEmaasEnergyUsageComponentDataAreaV2,
  IEmaasEnergyUsageHOCProps,
  ISiteCard
} from '../../IEnergyManagementProps';
import FilterScope1Action from '../../../../context/FilterScope1Context/FilterScope1Actions';
import { useInterval } from '../../../shared/useInterval/useInterval';
import {
  POLLING_MILLI_SECONDS,
  POLLING_SECONDS
} from '../../../shared/Utils/Constants';
import EmaasBasicSubscriptionEnergyUsageComponentSite from '../EmaasBasicSubscriptionEnergyUsageComponent/EmaasBasicSubscriptionEnergyUsageComponentSite';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../shared/Enums';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { PollingContext } from '../../../../context/PollingContext/PollingContext';
import { getIntervalType } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { useFilterScope1Context } from '../../../../context/FilterScope1Context/FilterScope1Context';
import EmaasNILMEnergyUsageComponent from '../EmaasNILMEnergyUsageComponent/EmaasNILMEnergyUsageComponent';
import { Card, Icon, Tab, Tooltip, Button, Select } from '@scuf/common';
import NoData from '../../../shared/ErrorComponents/NoData';
import moment from 'moment';

import EnergyIconActive from '../../../../images/EnergyIconActive.svg';
import EnergyIconInActive from '../../../../images/EnergyIconInActive.svg';
import WaterIconActive from '../../../../images/WaterIconActive.svg';
import WaterIconInActive from '../../../../images/WaterIconInActive.svg';
import { ApiContext } from '../../../../context/ApiContext';
import {
  IIconTabbedSwitchProps,
  IconTabbedSwitch
} from '../../../shared/TabbedSwitch/IconTabbedSwitch/IconTabbedSwitch';
import EmaasEnergyUsageComponentSeparate from '../EmaasEnergyUsageComponent/EmaasEnergyUsageComponentSeparate';
import BreakdownByMeter from '../../../shared/BreakdownByMeter/BreakdownByMeter';
import './EmaasEnergyUsageHOCSiteSeparate.scss';

interface IEmaasEnergyUsageHOCContent extends ISiteCard {
  data:
    | IEmaasEnergyUsageComponentDataArea
    | IEmaasEnergyUsageComponentDataAreaV2;
  emaasContext?: IAny;
  forGraph?: boolean;
  forSummary?: boolean;
  forTable?: boolean;
}
interface activeIndexProps {
  activeIndex: number;
}
const EmaasEnergyUsageHOCContent = <T extends activeIndexProps>(
  props: IEmaasEnergyUsageHOCContent & T
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, emaasContext, activeIndex } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <>
      {data.subscriptionType === EnergyUsageField.BASICSUBS ? (
        activeIndex === 1 ? (
          <EmaasNILMEnergyUsageComponent
            data={data as IEmaasEnergyUsageComponentDataAreaV2}
            viewType={EnergyUsageField.NILM}
            emaasContext={emaasContext}
          />
        ) : (
          <EmaasBasicSubscriptionEnergyUsageComponentSite
            data={data}
            viewType={EnergyUsageField.SITEENERGY}
          />
        )
      ) : (
        <EmaasEnergyUsageComponentSeparate
          data={data}
          forSummary={props.forSummary}
          forGraph={props.forGraph}
          forTable={props.forTable}
        />
      )}
    </>
  );
};

const EmaasEnergyUsageHOCWithLoader = withLoadingStatus(
  withErrorStatus(EmaasEnergyUsageHOCContent)
);

// Refresh every hour
const ENERGYUSAGE_REFRESH_INTERVAL = POLLING_SECONDS * POLLING_MILLI_SECONDS;
const EmaasEnergyUsageHOCSiteSeparateAPI = (
  props: IEmaasEnergyUsageHOCProps
) => {
  const {
    bearerToken,
    requestContext,
    emaasContext,
    dataSourceType,
    setDataSourceType
  } = props;

  const apiContext = useContext(ApiContext);

  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    apiContext.toggleSeparateApi &&
    dataSourceType !== EnergyUsageDataSourceType.UTILITY_METER;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);
  const [activeIndex, setIndexTab] = useState(0);
  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!,
    emaasContext,
    requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
      emaasContext.isNILMSubscribed &&
      emaasContext.toggleFeatureNILM2 &&
      activeIndex === 0,
    dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!,
    emaasContext,
    requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
      emaasContext.isNILMSubscribed &&
      emaasContext.toggleFeatureNILM2 &&
      activeIndex === 0
  );

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    console.log(
      " eDate.diff(sDate, 'days') <= 365",
      moment(eDate).diff(sDate, 'days')
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const changeTabIndex = (index: number) => {
    if (index != activeIndex) {
      setIndexTab(index);
      setIsLoading(true);
    }
  };
  const getConnectorConfigName = () => {
    if (
      requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
      emaasContext.isNILMSubscribed &&
      emaasContext.toggleFeatureNILM2 &&
      activeIndex === 1
    ) {
      return 'emaas-energy-Disaggregation';
    } else if (
      requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
      activeIndex === 0
    ) {
      return 'emaas-energy-usage';
    } else if (isSitePremium) {
      return 'emaas-energy-usage-summary';
    } else {
      return 'emaas-energy-usage';
    }
  };
  /*
  const connectorConfig = {
    name: getConnectorConfigName(),
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      intervalType: getIntervalType(startDate, endDate)
    }
  };
  */

  const [currentTab, setCurrentTab] = useState<string>('All');

  const connectorConfigPerformanceAggregation: IAny = useMemo(() => {
    if (currentTab === 'All') {
      return {
        name: 'emaas-energy-usage-performance-aggregation',
        options: {
          startDate: startDate,
          endDate: endDate,
          allDetails: 'true',
          intervalType: getIntervalType(startDate, endDate),
          dataSource: dataSourceType || ''
        }
      };
    }
    return {
      name: 'emaas-energy-usage-performance-aggregation',
      options: {
        startDate: startDate,
        endDate: endDate,
        allDetails: 'true',
        intervalType: getIntervalType(startDate, endDate),
        utilityType: currentTab // Use state variable
      }
    };
  }, [startDate, endDate, currentTab, dataSourceType]);

  const connectorConfigTable: IAny = useMemo(() => {
    if (currentTab === 'All') {
      return {
        name: 'emaas-energy-usage-table',
        options: {
          startDate: startDate,
          endDate: endDate,
          allDetails: 'true',
          groupType: 'Area',
          intervalType: getIntervalType(startDate, endDate),
          dataSource: dataSourceType || ''
        }
      };
    }
    return {
      name: 'emaas-energy-usage-table',
      options: {
        startDate: startDate,
        endDate: endDate,
        allDetails: 'true',
        groupType: 'Area',
        intervalType: getIntervalType(startDate, endDate),
        utilityType: currentTab // Use state variable
      }
    };
  }, [startDate, endDate, currentTab, dataSourceType]);

  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  // Water and Energy Tab
  const availableTabs: IIconTabbedSwitchProps['tabList'] = [
    {
      id: 'All',
      label: t(d.ENERGYTAB),
      iconSVG: EnergyIconActive,
      iconInactiveSVG: EnergyIconInActive
    },
    {
      id: 'Water',
      label: t(d.WATERTAB),
      iconSVG: WaterIconActive,
      iconInactiveSVG: WaterIconInActive
    }
  ];

  const connectorConfig: IAny = useMemo(() => {
    if (currentTab === 'All') {
      return {
        name: getConnectorConfigName(),
        options: {
          startDate: startDate,
          endDate: endDate,
          allDetails: 'true',
          intervalType: getIntervalType(startDate, endDate),
          dataSource: dataSourceType || ''
        }
      };
    }
    return {
      name: getConnectorConfigName(),
      options: {
        startDate: startDate,
        endDate: endDate,
        allDetails: 'true',
        intervalType: getIntervalType(startDate, endDate),
        utilityType: currentTab // Use state variable
      }
    };
  }, [startDate, endDate, currentTab, dataSourceType]);

  const [fetchedData] = useConnectorAndDynamicProps({
    connectorConfig: connectorConfig,
    requestContext,
    bearerToken
  });

  let dataConnectorPerformanceAggregation: any;
  //if (isSitePremium) {
  dataConnectorPerformanceAggregation = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigPerformanceAggregation,
    requestContext,
    bearerToken
  })[0];
  //}

  let dataConnectorTable: any;
  //if (isSitePremium) {
  dataConnectorTable = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigTable,
    requestContext,
    bearerToken
  })[0];
  //}

  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [pollingCount, setPollingCount] = useState(0);

  const isMounted = useMounted();

  useEffect(() => {
    setIsSilentLoading(false);
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        onRefresh();
        onRefreshPerf();
        onRefreshTable();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext, activeIndex]);

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [pollingCount]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  const { all, loading, error, status } = fetchedData.data;

  const EmaasEnergyUsage = (all as unknown) as IEmaasEnergyUsageComponentDataArea;

  // Polling mechanism 15 min
  useInterval(() => {
    if (EmaasEnergyUsage?.subscriptionType === EnergyUsageField.PREMIUMSUBS) {
      setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, ENERGYUSAGE_REFRESH_INTERVAL);

  // const removeSilentLoadingonRefresh = () => {
  //   setIsSilentLoading(false);
  //   return onRefresh();
  // };

  const onRefreshPerf = () =>
    dataConnectorPerformanceAggregation &&
    dataConnectorPerformanceAggregation.actions &&
    dataConnectorPerformanceAggregation.actions.getAll();

  const onRefresh = () =>
    fetchedData && fetchedData.actions && fetchedData.actions.getAll();

  const onRefreshTable = () =>
    dataConnectorTable &&
    dataConnectorTable.actions &&
    dataConnectorTable.actions.getAll();

  const allTable = dataConnectorTable.data.all;
  const loadingDataTable = dataConnectorTable.data.loading;
  const errorDataTable = dataConnectorTable.data.error;
  const statusDataTable = dataConnectorTable.data.status;

  const allPerf = dataConnectorPerformanceAggregation.data.all;
  const loadingPerf = dataConnectorPerformanceAggregation.data.loading;
  const errorPerf = dataConnectorPerformanceAggregation.data.error;
  const statusPerf = dataConnectorPerformanceAggregation.data.status;

  const PerformanceData = (allPerf as unknown) as IEmaasEnergyUsageComponentDataArea;
  const isDataAvailableForPerfromance =
    PerformanceData && PerformanceData.performanceAggregation ? true : false;

  const TableData = (allTable as unknown) as IEmaasEnergyUsageComponentDataArea;
  const isDataAvailableForTable =
    TableData && TableData.areaWiseAggregations ? true : false;

  const electricitySavings = (all as unknown) as IEmaasEnergyUsageComponentDataArea;
  const isDataAvailable =
    electricitySavings && electricitySavings.summary ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IEmaasEnergyUsageComponentDataArea);
  const [statusCodePerf, setStatusCodePerf] = useState<number | null>(0);
  const [dataPerf, setDataPerf] = useState(
    {} as IEmaasEnergyUsageComponentDataArea
  );
  const [isLoadingPerf, setIsLoadingPerf] = useState<boolean>(loadingPerf);
  const [errorInfoPerf, setErrorInfoPerf] = useState(errorPerf);

  const [tableData, setTableData] = useState(
    {} as IEmaasEnergyUsageComponentDataArea
  );
  const [loadingTable, setLoadingTable] = useState<boolean>(loadingDataTable);
  const [errorTable, setErrorTable] = useState(errorDataTable);
  const [statusCodeTable, setStatusCodeTable] = useState<number | null>(0);

  const {
    enableWaterUsageDashboards,
    enableConsumptionSourceSelect,
    cemsIntervalEnergySourceOption
  } = apiContext;

  useEffect(() => {
    console.log(
      '====useEffectCheckMounted',
      isMounted,
      currentTab,
      dataSourceType
    );
    if (isMounted) {
      onRefresh();
      onRefreshPerf();
      onRefreshTable();
    }
  }, [currentTab, dataSourceType]);

  useEffect(() => {
    if (enableConsumptionSourceSelect) {
      if (currentTab === 'water') {
        setDataSourceType &&
          setDataSourceType(EnergyUsageDataSourceType.LIVE_METER);
      }
    }
  }, [enableConsumptionSourceSelect, currentTab]);

  useEffect(() => {
    setIsLoadingPerf(loadingPerf);
    if (
      isDataAvailableForPerfromance ||
      errorPerf ||
      statusPerf ||
      !loadingPerf
    ) {
      if (isDataAvailableForPerfromance) {
        let tempData: any = PerformanceData;
        setDataPerf(tempData);
        setPollingTime && setPollingTime();
        dispatch(
          FilterScope1Action.subscriptionValue(
            EmaasEnergyUsage.subscriptionType as IAny
          )
        );
      }
      setErrorInfoPerf(errorPerf);
      setStatusCodePerf(statusPerf);
    }
  }, [isDataAvailableForPerfromance, loadingPerf, statusPerf]);

  useEffect(() => {
    setIsLoading(loading);

    if (isDataAvailable || error || status || !loading) {
      if (isDataAvailable) {
        let tempData = electricitySavings;
        setData(tempData);
        setPollingTime && setPollingTime();
        dispatch(
          FilterScope1Action.subscriptionValue(
            EmaasEnergyUsage.subscriptionType as IAny
          )
        );
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  useEffect(() => {
    setLoadingTable(loadingDataTable);

    if (
      isDataAvailableForTable ||
      errorDataTable ||
      statusDataTable ||
      !loadingDataTable
    ) {
      if (isDataAvailableForTable) {
        let tempData: any = TableData;
        setTableData(tempData);
        setPollingTime && setPollingTime();
        dispatch(
          FilterScope1Action.subscriptionValue(
            EmaasEnergyUsage.subscriptionType as IAny
          )
        );
      }
      setErrorTable(errorDataTable);
      setStatusCodeTable(statusDataTable);
    }
  }, [isDataAvailableForTable, loadingDataTable, statusDataTable]);

  const [state, dispatch] = useFilterScope1Context();
  const pollingContext = useContext(PollingContext);
  const { setPollingTime } = pollingContext;

  return (
    <div
      id="cems-site-energy-usage-card"
      className="emaas-energyusage-container kpi-card-common cems-site-energy-usage-card"
    >
      <Card>
        {requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
        emaasContext.isNILMSubscribed &&
        emaasContext.toggleFeatureNILM2 ? (
          <Card.Header className="kpi-header-container basicNilmHeader">
            <Tab
              className="tabForSiteDashboard"
              defaultActiveIndex={activeIndex}
              onTabChange={(activeIndex) => changeTabIndex(activeIndex)}
            >
              <Tab.Pane title={t(d.ENERGYUSAGE)} key={0}></Tab.Pane>
              <Tab.Pane title={t(d.ENERGYDISAGGREGATION)} key={1}></Tab.Pane>
            </Tab>
            <Tooltip
              element={
                <Icon root="common" name="badge-info" size="small" key={1} />
              }
              event="hover"
              header={
                activeIndex === 1 ? t(d.NILMTOOLTIPHEADER) : t(d.ENERGYUSAGE)
              }
              content={
                activeIndex === 1
                  ? t(d.EMAASNILMTOOLTIP)
                  : requestContext.context.subscriptionType ===
                    'BASIC_SUBSCRIPTION'
                  ? t(d.EMAASENERGYUSAGETOOLTIPPREV)
                  : t(d.EMAASENERGYUSAGETOOLTIP)
              }
              position="right center"
              size="small"
            />
          </Card.Header>
        ) : (
          <Card.Header className="kpi-header-container consumption-header-with-tabs">
            <div className="kpi-card-title-wrapper">
              <div style={{ marginRight: '5px' }} className="kpi-card-title">
                {enableWaterUsageDashboards
                  ? t(d.ENERGYUSAGECONSUMPTION)
                  : t(d.ENERGYUSAGE)}
              </div>
              <Tooltip
                element={
                  <Icon
                    style={{ marginBottom: '4px' }}
                    root="common"
                    name="badge-info"
                    size="small"
                    key={1}
                  />
                }
                event="hover"
                header={
                  enableWaterUsageDashboards
                    ? t(d.ENERGYUSAGECONSUMPTION)
                    : t(d.ENERGYUSAGE)
                }
                content={
                  requestContext.context.subscriptionType ===
                  'BASIC_SUBSCRIPTION'
                    ? t(d.EMAASENERGYUSAGETOOLTIPPREV)
                    : enableConsumptionSourceSelect
                    ? dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                      ? t(d.EMAASENERGYUSAGETOOLTIPPREV)
                      : dataSourceType ===
                        EnergyUsageDataSourceType.UTILITY_INTERVAL
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_INTERVAL_TOOLTIP)
                      : enableWaterUsageDashboards && currentTab == 'water'
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_LIVE_WATER_TOOLTIP)
                      : t(d.CEMSPORTFOLIO_DATA_SOURCE_LIVE_TOOLTIP)
                    : enableWaterUsageDashboards
                    ? t(d.EMAASCONSUMPTIONTOOLTIP)
                    : t(d.EMAASENERGYUSAGETOOLTIP)
                }
                position="right center"
                size="small"
              />
            </div>
            <div>
              {enableWaterUsageDashboards &&
                (enableConsumptionSourceSelect ? (
                  <Select
                    className="kpi-card-select cems-energy-water-select"
                    options={[
                      {
                        value: 'All',
                        text: t(d.ENERGYNOUNIT)
                      },
                      {
                        value: 'water',
                        text: t(d.WATER)
                      }
                    ]}
                    value={currentTab}
                    onChange={(value) => setCurrentTab(value as string)}
                  />
                ) : (
                  <IconTabbedSwitch
                    activeTab={currentTab}
                    hideInactiveText
                    tabList={availableTabs}
                    onTabChange={(id) => {
                      setCurrentTab(id);
                    }}
                  />
                ))}
              {enableConsumptionSourceSelect && (
                <Select
                  className="kpi-card-select cems-energy-datasource-select"
                  options={[
                    {
                      value: EnergyUsageDataSourceType.UTILITY_METER,
                      text: t(d.UTILITYPROVIDERNEW),
                      disabled: currentTab === 'water'
                    },
                    ...(cemsIntervalEnergySourceOption
                      ? [
                          {
                            value: EnergyUsageDataSourceType.UTILITY_INTERVAL,
                            text: t(d.UTILITYINTERVAL),
                            disabled: currentTab === 'water'
                          }
                        ]
                      : []),
                    {
                      value: EnergyUsageDataSourceType.LIVE_METER,
                      text: t(d.LIVEREADING)
                    }
                  ]}
                  value={dataSourceType}
                  defaultValue={EnergyUsageDataSourceType.UTILITY_METER}
                  onChange={(value) =>
                    setDataSourceType &&
                    setDataSourceType(value as EnergyUsageDataSourceType)
                  }
                />
              )}
            </div>
          </Card.Header>
        )}
        <Card.Content className="kpi-content-container with-energy-star">
          {isOutOfRange ? (
            <div className="noDataDisplay common-error-container">
              <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
            </div>
          ) : (
            <>
              {statusCode == 204 || statusCodePerf == 204 ? (
                <Card.Content className="kpi-content-container">
                  <EmaasEnergyUsageHOCWithLoader
                    loading={isLoading}
                    error={errorInfo}
                    statusCode={204}
                    onRefresh={onRefresh}
                    data={data}
                    loadingText={t(d.LOADING)}
                    title={t(d.CONNECTIONERRORTITLE)}
                    noDataMessage={t(d.NODATAAVAILABLE)}
                    connectionErrorMessage={t(d.CONNECTIONERROR)}
                    silentLoading={silentLoading}
                    emaasContext={emaasContext}
                    activeIndex={activeIndex}
                    forSummary={false}
                  />
                </Card.Content>
              ) : (
                <>
                  <Card.Content className="kpi-content-container siteview-progressbar">
                    <EmaasEnergyUsageHOCWithLoader
                      loading={isLoading}
                      error={errorInfo}
                      statusCode={statusCode}
                      onRefresh={onRefresh}
                      data={data}
                      loadingText={t(d.LOADING)}
                      title={t(d.CONNECTIONERRORTITLE)}
                      noDataMessage={t(d.NODATAAVAILABLE)}
                      connectionErrorMessage={t(d.CONNECTIONERROR)}
                      silentLoading={silentLoading}
                      emaasContext={emaasContext}
                      activeIndex={activeIndex}
                      forSummary={true}
                    />
                  </Card.Content>
                  <Card.Content className="kpi-content-container siteview-progressbar">
                    <EmaasEnergyUsageHOCWithLoader
                      loading={isLoadingPerf}
                      error={errorInfoPerf}
                      statusCode={statusCodePerf}
                      onRefresh={onRefreshPerf}
                      data={dataPerf}
                      loadingText={t(d.LOADING)}
                      title={t(d.CONNECTIONERRORTITLE)}
                      noDataMessage={t(d.NODATAAVAILABLE)}
                      connectionErrorMessage={t(d.CONNECTIONERROR)}
                      silentLoading={silentLoading}
                      emaasContext={emaasContext}
                      activeIndex={activeIndex}
                      forGraph={true}
                    />
                  </Card.Content>
                  <Card.Content className="kpi-content-container siteview-progressbar">
                    {statusCodeTable === 204 ? (
                      <div className="no-data-container">
                        <h1 className="desc">{t(d.NODATAAVAILABLE)}</h1>
                      </div>
                    ) : (
                      <EmaasEnergyUsageHOCWithLoader
                        loading={loadingTable}
                        error={errorTable}
                        statusCode={statusCodeTable}
                        onRefresh={onRefreshTable}
                        data={tableData}
                        loadingText={t(d.LOADING)}
                        title={t(d.CONNECTIONERRORTITLE)}
                        noDataMessage={t(d.NODATAAVAILABLE)}
                        connectionErrorMessage={t(d.CONNECTIONERROR)}
                        silentLoading={silentLoading}
                        emaasContext={emaasContext}
                        activeIndex={activeIndex}
                        forTable={true}
                      />
                    )}
                  </Card.Content>
                </>
              )}
            </>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};
export default EmaasEnergyUsageHOCSiteSeparateAPI;
